import React, { useCallback, useEffect, useRef } from 'react';
import { getDocument } from '@atlaskit/browser-apis';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import {
	IssueUpdateSuggestionsSubscription,
	type NudgeMetadata,
} from '@jira/ai-rovo__subscription-issue-update-suggestions/src/IssueUpdateSuggestionsSubscription.tsx';
import { useAiRovoCurrentRouteConfigurationStoreActions } from '@jira/ai-rovo_actions-store/src/aiRovoCurrentRouteConfigurationStore.tsx';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { useProjectContext } from '@jira/platform__services-providers-project-context';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';

const nudgeTitleMessage = defineMessage({
	defaultMessage: 'You have {numberOfSuggestions} work item updates',
	description: 'The title of the nudge shown to users when there are work item updates available.',
	id: 'ai-rovo.actions-software-backlog.nudge-title.nonfinal',
});

const nudgeDescriptionMessage = defineMessage({
	defaultMessage: 'We have identified {numberOfSuggestions} work item updates',
	description:
		'The description of the nudge shown to users when there are work item updates available.',
	id: 'ai-rovo.actions-software-backlog.nudge-description.nonfinal',
});

const nudgeIconLabelMessage = defineMessage({
	defaultMessage: 'Work items icon',
	description: 'Label for the work items icon used in the nudge.',
	id: 'ai-rovo.actions-software-backlog.nudge-icon-label.nonfinal',
});

type Props = {
	sourceId: string;
};

/**
 * A React component that provides project-scoped nudge subscription functionality for AI Rovo.
 *
 * This component subscribes to issue update suggestions events within a specific project context
 * and displays nudges to users when suggestions are available. When a nudge is clicked, it opens
 * the AI chat interface in mini-modal mode.
 *
 * @param props - The component properties
 * @param props.sourceId - The source identifier used for tracking and publishing events
 *
 * @returns The subscription component if project context is valid,
 *          otherwise returns null
 *
 * @remarks
 * - Depends on project context data being available and loaded
 * - Creates nudges with issue update suggestions count and handles click events to open chat
 * - Uses internationalization for nudge titles and descriptions
 * - Defers applying nudges when the tab is not active: a nudge is only applied when the tab is
 *   visible (Page Visibility API) AND the document has focus (document.hasFocus()). If an event
 *   arrives while either condition is not met, the nudge is stored and applied once the tab
 *   becomes visible or the window regains focus
 */
export const AiRovoProjectScopedNudgeSubscriptionProvider = ({
	sourceId,
}: Props): React.JSX.Element | null => {
	const { data: projectData, error, loading } = useProjectContext();
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');
	const { updateConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();
	const lastReceivedNudgeRef = useRef<NudgeMetadata | null>(null);

	const applyNudge = useCallback(
		(metadata: NudgeMetadata) => {
			const { channelConversationId, numberOfSuggestions } = metadata;
			const iconNode = <WorkItemsIcon size="medium" label={formatMessage(nudgeIconLabelMessage)} />;

			updateConfiguration({
				nudges: [
					{
						id: `issue-update-suggestions-rovo-nudge-${channelConversationId}`,
						title: formatMessage(nudgeTitleMessage, { numberOfSuggestions }),
						description: formatMessage(nudgeDescriptionMessage, { numberOfSuggestions }),
						icon: () => iconNode,
						onClick: () => {
							publish({
								type: 'chat-open' as const,
								product: 'jira' as const,
								data: {
									channelId: channelConversationId,
								},
								openChatMode: 'mini-modal' as const,
								source: sourceId,
							});
						},
						type: 'ai-action',
						handleType: HandleActionType.Base,
					},
				],
			});
		},
		[formatMessage, publish, sourceId, updateConfiguration],
	);

	useEffect(() => {
		const doc = getDocument();
		if (!doc) {
			return;
		}

		const isTabVisible = () => doc.visibilityState === 'visible';
		const isTabActive = () => isTabVisible() && doc.hasFocus();

		const applyPendingNudge = () => {
			const pending = lastReceivedNudgeRef.current;
			if (pending) {
				applyNudge(pending);
				lastReceivedNudgeRef.current = null;
			}
		};

		const onVisibilityChange = () => {
			if (isTabActive()) {
				applyPendingNudge();
			}
		};

		const onWindowFocus = () => {
			if (isTabVisible()) {
				applyPendingNudge();
			}
		};

		doc.addEventListener('visibilitychange', onVisibilityChange);

		const win = typeof globalThis.window !== 'undefined' ? globalThis.window : null;
		if (win) {
			win.addEventListener('focus', onWindowFocus);
		}

		return () => {
			doc.removeEventListener('visibilitychange', onVisibilityChange);
			if (win) {
				win.removeEventListener('focus', onWindowFocus);
			}
		};
	}, [applyNudge]);

	const handleEventReceived = useCallback(
		(nudgeMetadata: NudgeMetadata) => {
			lastReceivedNudgeRef.current = nudgeMetadata;
			const doc = getDocument();
			if (doc?.visibilityState === 'visible' && doc.hasFocus()) {
				applyNudge(nudgeMetadata);
				lastReceivedNudgeRef.current = null;
			}
		},
		[applyNudge],
	);

	if (
		!fg('jira-ai-proactive-loom-updates-suggestions') ||
		fg('post-office_update-work-rovo-nudge')
	) {
		return null;
	}

	if (loading || error || !projectData?.projectId) {
		return null;
	}

	return (
		<JSErrorBoundary
			id="rovo-issue-update-suggestions-subscription"
			packageName="@jira/ai-rovo_actions-route-provider"
			teamName="reverie"
			fallback="unmount"
		>
			<UFOSegment name="rovo-issue-update-suggestions-subscription">
				<IssueUpdateSuggestionsSubscription
					appId={sourceId}
					projectId={String(projectData.projectId)}
					onEventReceived={handleEventReceived}
				/>
			</UFOSegment>
		</JSErrorBoundary>
	);
};
